import { Button, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconChevronRight, IconUser, IconUsers } from "@tabler/icons-react"
import { DefaultModal } from "@components/default-modal"
import { useTranslation } from "react-i18next"
import { Page } from "../../../../../-components/Page"
import {
  useCanAddPersonalCalender,
  useGetDatabaseRules,
  WebCalendarProfileDto,
} from "@/orval/gen"
import { useGetAuthenticatedUser } from "@/routes/app/-components/use-authenticated-user"
import { useIsSmallScreen } from "@/utils/useIsSmallScreen"
import { CalendarStack } from "../-calendar-list"
import {
  CreateCalendarProfileModal,
  EditCalendarProfileModal,
} from "./CalendarProfileModal"

export function CalendarSubscriptionsPage() {
  const { t } = useTranslation("app_server")
  const isSmallScreen = useIsSmallScreen()
  const [typeChoiceOpened, typeChoiceModal] = useDisclosure()
  const [personalOpened, personalModal] = useDisclosure()
  const [sharedOpened, sharedModal] = useDisclosure()
  const [selectedProfile, setSelectedProfile] =
    React.useState<WebCalendarProfileDto | null>(null)
  const { data: authenticatedUser } = useGetAuthenticatedUser()
  const { data: canAdd } = useCanAddPersonalCalender({
    query: { enabled: !!authenticatedUser },
  })
  const { data: databaseRules } = useGetDatabaseRules({
    query: { enabled: !!authenticatedUser },
  })
  const canCreateShared =
    databaseRules?.includes("CreatePublicWebCalendarProfiles") ?? false
  const canCreate = canAdd || canCreateShared

  const handleCreate = () => {
    if (canAdd && canCreateShared) {
      typeChoiceModal.open()
    } else if (canAdd) {
      personalModal.open()
    } else if (canCreateShared) {
      sharedModal.open()
    }
  }

  return (
    <Page
      infoDescription={t("Calendar_Subscriptions_Description")}
      dottedMenuProps={
        isSmallScreen && canCreate
          ? {
              size: "xl",
              items: [
                {
                  children: t("Calendar_Subscriptions_CreateCalendar"),
                  onClick: handleCreate,
                },
              ],
            }
          : undefined
      }
      buttonGroupProps={
        isSmallScreen || !canCreate
          ? undefined
          : {
              children: (
                <Button size="xs" onClick={handleCreate}>
                  {t("Calendar_Subscriptions_CreateCalendar")}
                </Button>
              ),
            }
      }
    >
      {typeChoiceOpened && canAdd && canCreateShared && (
        <DefaultModal
          opened
          fullScreen={false}
          title={t("Calendar_Subscriptions_CreateCalendar")}
          onClose={typeChoiceModal.close}
        >
          <Stack gap="sm">
            <Button
              variant="default"
              h="auto"
              p="md"
              leftSection={<IconUser size={24} />}
              rightSection={<IconChevronRight size={18} />}
              styles={{
                label: { flex: 1, whiteSpace: "normal", textAlign: "left" },
              }}
              onClick={() => {
                typeChoiceModal.close()
                personalModal.open()
              }}
            >
              <Stack component="span" gap={4}>
                <Text component="span" fw={500}>
                  {t("Calendar_Subscriptions_PersonalCalendar")}
                </Text>
                <Text component="span" size="sm" c="dimmed" fw={400}>
                  {t("Calendar_Subscriptions_PersonalCalendarDescription")}
                </Text>
              </Stack>
            </Button>
            <Button
              variant="default"
              h="auto"
              p="md"
              leftSection={<IconUsers size={24} />}
              rightSection={<IconChevronRight size={18} />}
              styles={{
                label: { flex: 1, whiteSpace: "normal", textAlign: "left" },
              }}
              onClick={() => {
                typeChoiceModal.close()
                sharedModal.open()
              }}
            >
              <Stack component="span" gap={4}>
                <Text component="span" fw={500}>
                  {t("Calendar_Subscriptions_SharedCalendar")}
                </Text>
                <Text component="span" size="sm" c="dimmed" fw={400}>
                  {t("Calendar_Subscriptions_SharedCalendarDescription")}
                </Text>
              </Stack>
            </Button>
          </Stack>
        </DefaultModal>
      )}
      {personalOpened && (
        <CreateCalendarProfileModal
          isShared={false}
          onClose={personalModal.close}
        />
      )}
      {sharedOpened && (
        <CreateCalendarProfileModal isShared onClose={sharedModal.close} />
      )}
      {selectedProfile && (
        <EditCalendarProfileModal
          key={selectedProfile.webCalendarProfileId}
          profile={selectedProfile}
          onClose={() => setSelectedProfile(null)}
        />
      )}
      <CalendarStack onEditProfileClick={setSelectedProfile} />
    </Page>
  )
}
